import React, { useState, useEffect, useCallback, useRef } from "react";
import "./Gallery.css";
import { motion, useAnimation, useInView } from "framer-motion";

export default function Gallery({ images = [], className }) {
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const galleryRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize(); // Ensure it runs initially
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!galleryRef.current) return;

      const galleryRect = galleryRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (galleryRect.bottom > 0 && galleryRect.top < viewportHeight) {
        let scrollPercentage =
          window.scrollY /
          (document.documentElement.scrollHeight - viewportHeight);
        scrollPercentage = Math.min(Math.max(scrollPercentage, 0), 1); // Keep it in range

        const maxScrollLeft =
          galleryRef.current.scrollWidth - galleryRef.current.offsetWidth;
        const multiplier = isMobile ? 4 : 2;

        galleryRef.current.scrollLeft =
          scrollPercentage * maxScrollLeft * multiplier;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMobile]);

  const openLightbox = (index) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };

  const closeLightbox = () => {
    setIsOpen(false);
  };

  // Memoize nextImage and prevImage with useCallback to prevent them from being recreated on each render
  const nextImage = useCallback(() => {
    setPhotoIndex((photoIndex + 1) % images.length);
  }, [photoIndex, images.length]);

  const prevImage = useCallback(() => {
    setPhotoIndex((photoIndex + images.length - 1) % images.length);
  }, [photoIndex, images.length]);

  // Listen for keyboard arrow key events
  useEffect(() => {
    if (!isOpen) return;

    const handleKeydown = (event) => {
      if (event.key === "ArrowRight") {
        nextImage();
      } else if (event.key === "ArrowLeft") {
        prevImage();
      } else if (event.key === "Escape") {
        closeLightbox(); // Close lightbox on 'Escape'
      }
    };

    // Add event listener for keydown
    window.addEventListener("keydown", handleKeydown);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener("keydown", handleKeydown);
    };
  }, [isOpen, photoIndex, nextImage, prevImage]); // Added nextImage and prevImage to dependency array

  return (
    <div
      className={`overflow-hidden [transition:all_ease_0.5s] ${className}`}
      ref={galleryRef}
    >
      {/* Main Grid with Groups */}
      <div className="w-[130vw] grid max-xsm:grid-cols-4 max-md:grid-cols-5 grid-cols-8 gap-[3px]">
        {/* Use 1 or 2 per row depending on screen size */}
        {[...Array(Math.ceil(images.length / (isMobile ? 1 : 2)))]?.map(
          (_, index) => (
            <div key={index} className="grid gap-[3px]">
              {images
                .slice(
                  index * (isMobile ? 1 : 2),
                  index * (isMobile ? 1 : 2) + (isMobile ? 1 : 2)
                )
                .map((image, subIndex) => (
                  <div
                    key={subIndex}
                    className="group relative overflow-hidden Typography.Smp"
                    onClick={() =>
                      openLightbox(index * (isMobile ? 1 : 2) + subIndex)
                    }
                  >
                    <img
                      src={image.url}
                      alt="Gallery item"
                      className="h-full w-full object-cover Typography.Smp transition-transform transform scale-105 group-hover:scale-110 duration-500"
                    />
                    <div className="flex-col gap-[2px] duration-500 opacity-0 group-hover:opacity-100 hoverbox flex items-center justify-center absolute left-2 right-2 top-2 bottom-2 border-[1px] border-[#fff] bg-[#000] bg-opacity-30">
                      <p className="text-center text-[#fff] max-xsm:text-[12px] max-lg:text-[14px] text-[18px] font-[300] font-primary leading-[130%]">
                        Created by
                      </p>
                      <p className="text-center text-[#fff] max-xsm:text-[16px] max-lg:text-[17px] text-[26px] font-[500] font-primary leading-[130%]">
                        {image.artist}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          )
        )}
      </div>

      {/* Lightbox Modal */}
      {isOpen && (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-75 z-50 flex justify-center items-center animate-fadeIn"
          style={{ zIndex: 9999 }}
          onClick={closeLightbox} // Close when clicking outside the image
        >
          <div
            className="relative"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the image container
          >
            <img
              src={images[photoIndex].url}
              alt="Lightbox content"
              className="max-w-full max-h-screen object-contain animate-imageFadeIn"
            />
            <button
              onClick={closeLightbox}
              className="absolute top-0 right-0 text-white text-2xl p-4 bg-black bg-opacity-50 hover:bg-opacity-75 transition-all"
            >
              &times;
            </button>
            <button
              onClick={prevImage}
              className="absolute top-1/2 left-0 transform -translate-y-1/2 text-white text-4xl p-4 bg-black bg-opacity-50 hover:bg-opacity-75 transition-all"
            >
              &#10094;
            </button>
            <button
              onClick={nextImage}
              className="absolute top-1/2 right-0 transform -translate-y-1/2 text-white text-4xl p-4 bg-black bg-opacity-50 hover:bg-opacity-75 transition-all"
            >
              &#10095;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
